/**
 * Middle column — the agent panel. Legacy-faithful swap: shows the session list
 * OR the open thread (with a back control), never both. Owns the panel header
 * (sidebar-reveal when collapsed, back + active session title in thread mode) and
 * the session data (the World's sessions) feeding both the list and the header.
 */

import {
  type AttachmentRef,
  attachFile,
  createSession,
  deleteSession,
  isSessionDone,
  type MessageView,
  moveSession,
  renameSession,
  setSessionCompleted,
} from '@mod/core/model'
import { ArrowLeft, Columns2, PanelLeftOpen, X } from 'lucide-react'
import { useCallback, useMemo, useState } from 'react'
import {
  projectChoices,
  resolveCreateTarget,
} from '../../services/sessions/projectChoices'
import {
  defaultProject,
  readRecentProject,
  writeRecentProject,
} from '../../services/sessions/recentProject'
import { SessionMark } from '../../services/sessions/SessionMark'
import {
  STATUS_ROW_LIMIT,
  useSessionStatuses,
} from '../../services/sessions/sessionStatus'
import {
  startVoiceOnOpen,
  voiceBeforeSession,
} from '../../services/voice/startOnOpen'
import { useTabRepoBoot } from '../../services/world/repo/TabRepoProvider'
import { useComposerDevices } from '../../services/world/useComposerDevices'
import { useProjectHosting } from '../../services/world/useProjectHosting'
import { useWorldProjects } from '../../services/world/useWorldProjects'
import { useWorldSessions } from '../../services/world/useWorldSessions'
import { HeaderTitle } from '../../shell/window/HeaderTitle'
import { Panel, PanelAction } from '../../shell/window/Panel'
import { browserProcessImage, fileToSource } from './attachments'
import { Composer } from './Composer'
import { SessionList } from './SessionList'
import type { SessionRowData } from './SessionRow'
import { startSessionWithMessage } from './startSession'
import { ThreadView } from './ThreadView'
import { useSessionBack } from './useSessionBack'
import { useSessionList } from './useSessionList'

export function AgentPanel({
  projectId,
  listProjectId,
  selectedSessionId,
  onSelect,
  sidebarCollapsed,
  onShowSidebar,
  onSplit,
  onClose,
  onOpenDocument,
  messageView,
  focused = false,
}: {
  /**
   * Where a NEW session lands, and the fallback for a row whose project is not
   * known. Always a real project: creating a session needs a registry to write
   * it into, and the inbox is a view rather than a place.
   */
  projectId: string
  /**
   * Which list this block shows (RFC 0281 §6): one project's sessions, or
   * NULL for the cross-project inbox, which is what a bare `a` means.
   */
  listProjectId: string | null
  selectedSessionId: string | null
  /**
   * Picking a row. The second argument is the project that HOLDS that session,
   * which the inbox knows and a single-project list never had to say: the pane
   * opened for it must name that project or it resolves against the wrong
   * catalog and prunes itself.
   */
  onSelect: (sessionId: string | null, projectId?: string) => void
  sidebarCollapsed: boolean
  /**
   * Reveal the files sidebar. Absent on every pane but the one nearest it: the
   * sidebar is a single piece of chrome down the left edge, and a button to
   * reveal it repeated in the header of all four panes reads as four different
   * things rather than one. The shell decides which pane carries it.
   */
  onShowSidebar?: () => void
  /** Put a SECOND agent block beside this one, on the same session. Two
   *  conversations at once is what the content tree exists for; this is how you
   *  ask for it without discovering the drag. */
  onSplit?: () => void
  /** Close this pane. Absent when it is the only pane on screen: a project
   *  with nothing in it is not a state worth being able to reach by accident,
   *  and the tree would put an empty agent block straight back. */
  onClose?: () => void
  /** Put a document on the canvas (the shell owns the layout). Reaches the
   *  composer, so `/` can write a skill and open it. */
  onOpenDocument?: (documentId: string) => void
  /** Visible work captured by the shell for messages sent from this pane. */
  messageView?: MessageView | null
  focused?: boolean
}) {
  const boot = useTabRepoBoot()

  /**
   * Where the session header's widgets draw (RFC 0278).
   *
   * STATE, not a ref: the thread has to re-render when the node appears, and a
   * ref mutation does not tell it. Null on the first frame and while the list
   * is showing, which the thread reads as "draw in place" the way `ComposerSlot`
   * does.
   */
  const [sessionHeaderSlot, setSessionHeaderSlot] =
    useState<HTMLElement | null>(null)

  /**
   * ONE LIST, from one of two sources. Both hooks run every render because they
   * are hooks; the unused one is cheap, and the World's project documents are
   * already in the repo for the sidebar's directory.
   */
  // Every list is the World's, across projects (RFC 0357 paper cuts). A list
  // scoped to the pane's project showed only that project's sessions, and a
  // window that had followed a session into a test project showed three.
  //
  // The pane's own project is read in full beside it: the World's list keeps
  // only the newest 200 across every project, and does not list a project
  // the World has no entry for, so without this a session made here could
  // vanish from the list it was made in (review of PR 364, 2 and 3).
  const worldRows = useWorldSessions()
  const ownProjectId = listProjectId ?? projectId
  const { sessions: ownSessions, loading: ownLoading } =
    useSessionList(ownProjectId)
  const rows = useMemo(() => {
    const seen = new Set(worldRows.map((row) => row.id))
    const name =
      worldRows.find((row) => row.projectId === ownProjectId)?.projectName ?? ''
    const own = ownSessions
      .filter((row) => !seen.has(row.id))
      .map((row) => ({ ...row, projectId: ownProjectId, projectName: name }))
    return own.length
      ? [...worldRows, ...own].sort(
          (a, b) => (b.updatedAt ?? 0) - (a.updatedAt ?? 0),
        )
      : worldRows
  }, [worldRows, ownSessions, ownProjectId])
  // Until the pane's own project has arrived, the list is still loading:
  // "No sessions yet" on a cold window was a claim nothing had checked.
  const loading = ownLoading && rows.length === 0

  /**
   * Which project holds each row, for the writes below.
   *
   * Renaming or deleting from the INBOX has to reach the project that actually
   * lists that session. Sending it to this window's project instead would be a
   * write against a registry the session is not in: a silent no-op for a rename,
   * and for a delete, the wrong thing entirely.
   */
  const projectOf = useCallback(
    (sessionId: string): string => {
      // A session in a project the World does not list is still the pane's.
      return (
        worldRows.find((row) => row.id === sessionId)?.projectId ??
        listProjectId ??
        projectId
      )
    },
    [listProjectId, projectId, worldRows],
  )

  /** Where Back goes (RFC 0302 S7): the conversation a card was opened from,
   *  or the session list when the pane was not opened from one. */
  const back = useSessionBack(selectedSessionId, onSelect, projectOf)

  // Status for the rows being shown (RFC 0286), REGISTRY FIRST (RFC 0284 S8a).
  // The registry read above stays zero-hydration and now answers most rows;
  // this opens only the sessions it cannot answer for: one no runtime has
  // reported on, or whose claim went stale because its writer died. That is
  // what lets a run started on another device or from the CLI light its row.
  //
  // It used to open the top twenty regardless. Every one of those stayed in
  // wasm memory for the life of the window, in the tab and again in the shared
  // worker, and a fresh window was 3.4 GB before anything had been clicked.
  // Filed sessions are skipped: they are collapsed by default and nobody is
  // steering them, so paying a document each to say "done" is waste.
  const open = rows.filter((row) => !isSessionDone(row))
  const hydrated = useSessionStatuses(
    boot.repo,
    open
      .filter((row) => row.status === undefined)
      .slice(0, STATUS_ROW_LIMIT)
      .map((row) => row.id),
  )
  // The registry wins on a disagreement: a row was hydrated because its claim
  // was stale, and a fresh claim landed since. A plain map per render, since
  // `open` is rebuilt per render anyway and the rows are few.
  const statuses = new Map(hydrated)
  for (const row of open) {
    if (row.status !== undefined) statuses.set(row.id, row.status)
  }

  const sessions: SessionRowData[] = rows.map((row) => ({
    id: row.id,
    title: row.title,
    updatedAt: row.updatedAt,
    completedAt: row.completedAt,
    status: statuses.get(row.id),
    // What keeps a session an agent opened out of the top level. The status
    // above has to be resolved first: a sub-session stopped on a question
    // climbs back into the list, and that decision reads both fields.
    ...(row.parentSessionId ? { parentSessionId: row.parentSessionId } : {}),
    // `useWorldSessions` has carried the project on every row since RFC 0281
    // S2; each row says which project holds it.
    ...('projectName' in row
      ? { projectName: (row as { projectName: string }).projectName }
      : {}),
  }))
  const activeTitle = rows.find((r) => r.id === selectedSessionId)?.title

  /**
   * Where a new session MAY land (RFC 0290 S5).
   *
   * Every project in the World, with the ones no present device mounts shown
   * as unavailable and the reason attached. Safe to offer at all only because
   * a project host now exists per mounted project (S3): before that, choosing
   * a project nothing serves produced a session that was created, listed, and
   * permanently silent, which reads in the UI exactly like a busy one.
   */
  const worldProjects = useWorldProjects()
  const hostingOf = useProjectHosting()
  const choices = useMemo(
    () => projectChoices(worldProjects, hostingOf),
    [worldProjects, hostingOf],
  )

  /**
   * Why a plain `+` would refuse, when it would.
   *
   * A pane pinned to one project offers no menu, so without this the second
   * gate below would swallow the click and the button would do nothing at all,
   * which is the one outcome worse than refusing: it reads as broken rather
   * than as blocked. Undefined whenever creating is fine.
   */
  const createDisabledReason = useMemo(() => {
    const fallback = choices.find((c) => c.projectId === projectId)
    return fallback?.disabled ? fallback.reason : undefined
  }, [choices, projectId])

  // A new session lands in a REAL project even when the inbox is what is on
  // screen: the inbox is a view across registries, not one you can write to.
  const handleCreate = useCallback(
    (requested?: string) => {
      // The SECOND gate. The menu item being disabled is a fact about one
      // render; this is the rule, and it is the one that holds when the list is
      // stale or the device went quiet between the render and the click.
      // `touchSession` fills in a missing registry entry rather than skipping,
      // so an accepted write against an unserved project does not fail loudly:
      // it mints a plausible row and strands the session.
      // Unasked-for creates land where this person last worked, on this device
      // (RFC 0290 S6), falling back to the pane's own project. An explicit
      // `requested` from the menu always wins: a default is what to do when
      // nobody said, not a preference to override someone who did.
      const fallback = defaultProject(readRecentProject(), projectId, choices)
      const target = resolveCreateTarget(requested, fallback, choices)
      if (target === null) return
      void createSession(boot.repo, target, {
        title: `Session ${ownSessions.length + 1}`,
      }).then((h) => {
        writeRecentProject(target)
        onSelect(h.documentId as string, target)
      })
    },
    [boot.repo, projectId, ownSessions.length, onSelect, choices],
  )

  /**
   * Where the list's own composer will put the next session (RFC 0290 S6).
   *
   * Held here rather than derived on every render, because it is a CHOICE a
   * person can make before typing and it has to survive the renders between
   * choosing and sending. Seeded from the same default the create button uses,
   * so the two doors onto starting a session agree about where it lands.
   */
  const [startTarget, setStartTarget] = useState(() =>
    defaultProject(readRecentProject(), projectId, choices),
  )

  /**
   * The session list's own composer: a session that starts with the thing you
   * came to say, without pressing New first and typing into an empty thread.
   *
   * The new session shows in THIS block, so the list you were reading becomes
   * the conversation you just started rather than a pane opening beside it.
   *
   * The SAME gate as the create button, for the same reason. `startTarget` is a
   * choice made before typing and the world can move underneath it: a device
   * can stop beating between picking a project and pressing send. Sending
   * anyway would create a session in a project nothing hosts, and that failure
   * is silent.
   */
  // A file dropped on the start box goes out with the first message, the same
  // as in a thread (RFC 0288).
  const attach = useCallback(
    async (file: File): Promise<AttachmentRef> =>
      attachFile(await fileToSource(file), {
        processImage: browserProcessImage,
        repo: {
          create(doc) {
            return boot.repo.create(doc).documentId
          },
        },
      }),
    [boot.repo],
  )

  const handleStart = useCallback(
    async (
      text: string,
      requestedModel: { provider: string; model: string },
      skills?: string[],
      attachments?: AttachmentRef[],
    ) => {
      const target = resolveCreateTarget(startTarget, projectId, choices)
      if (target === null) return
      await startSessionWithMessage(
        boot.repo,
        target,
        {
          text,
          requestedModel,
          ...(skills?.length ? { skills } : {}),
          ...(attachments?.length ? { attachments } : {}),
        },
        (sessionId, projectId) => {
          writeRecentProject(projectId)
          onSelect(sessionId, projectId)
        },
      )
    },
    [boot.repo, startTarget, projectId, choices, onSelect],
  )

  const devices = useComposerDevices()

  // RFC 0307: the list's voice button makes a session, opens it in this block,
  // and the call starts in that session's own composer, as from the new pane.
  const [voiceStart, setVoiceStart] = useState<{
    starting: boolean
    error?: string
  }>({ starting: false })
  const listVoice = voiceBeforeSession(
    voiceStart,
    () => {
      const target = resolveCreateTarget(startTarget, projectId, choices)
      // One session per press: a second click while it is being made would make
      // a second "Voice call".
      if (target === null || voiceStart.starting) return
      setVoiceStart({ starting: true })
      void createSession(boot.repo, target, {
        title: 'Voice call',
        titleSetBy: 'stub',
      }).then(
        (h) => {
          const sessionId = h.documentId as string
          writeRecentProject(target)
          startVoiceOnOpen(sessionId)
          onSelect(sessionId, target)
          setVoiceStart({ starting: false })
        },
        (err: unknown) =>
          setVoiceStart({
            starting: false,
            error: `Voice could not start: ${err instanceof Error ? err.message : String(err)}`,
          }),
      )
    },
    () => setVoiceStart({ starting: false }),
  )

  /**
   * Move this session to another project, before it has said anything
   * (RFC 0290 S6).
   *
   * TWO WRITES, AND THE SECOND IS NOT OPTIONAL. `moveSession` changes which
   * registry lists the session. The PANE also has to follow, because a pane
   * pinned to a project resolves every session it shows to that project
   * (`projectOf` above returns the pane's id when it is not the inbox). Leaving
   * the pane behind would point the next send at a registry the session is no
   * longer in, and `touchSession` fills in a missing entry rather than skipping:
   * a phantom row in the old project, and a turn nobody promotes. Exactly the
   * failure this whole RFC is written against, reintroduced by a move that
   * forgot the window.
   */
  const handleMoveProject = useCallback(
    (target: string) => {
      if (!selectedSessionId) return
      const from = projectOf(selectedSessionId)
      if (from === target) return
      void moveSession(boot.repo, selectedSessionId, from, target).then(() => {
        // Remembered only after the move lands. Recording an intent that failed
        // would send the NEXT new session somewhere on the strength of
        // something that never happened.
        writeRecentProject(target)
        onSelect(selectedSessionId, target)
      })
    },
    [boot.repo, selectedSessionId, projectOf, onSelect],
  )

  const handleRename = useCallback(
    (sessionId: string, title: string) => {
      void renameSession(
        boot.repo,
        projectOf(sessionId),
        sessionId,
        title,
        'you',
      )
    },
    [boot.repo, projectOf],
  )

  const handleDelete = useCallback(
    (sessionId: string) => {
      if (sessionId === selectedSessionId) onSelect(null)
      void deleteSession(boot.repo, projectOf(sessionId), sessionId)
    },
    [boot.repo, projectOf, selectedSessionId, onSelect],
  )

  const handleComplete = useCallback(
    (sessionId: string, done: boolean) => {
      void setSessionCompleted(boot.repo, projectOf(sessionId), sessionId, done)
    },
    [boot.repo, projectOf],
  )

  return (
    <Panel
      focused={focused}
      framed={false}
      padded={false}
      header={
        <>
          {sidebarCollapsed && onShowSidebar && (
            <PanelAction title="Show files (⌘B)" onClick={onShowSidebar}>
              <PanelLeftOpen size={14} />
            </PanelAction>
          )}
          {/* A session view leads with its own back + title; the sessions
              home has no title of its own. */}
          {selectedSessionId && (
            <>
              <PanelAction
                title={
                  back.target
                    ? `Back to ${
                        rows.find((r) => r.id === back.target?.sessionId)
                          ?.title ?? 'the conversation this came from'
                      }`
                    : 'Back to sessions'
                }
                onClick={back.goBack}
              >
                <ArrowLeft size={15} />
              </PanelAction>
              {/* The session's mark beside its name (RFC 0311), the same one
                  its row draws, so the pane and the list agree on who this is. */}
              <SessionMark id={selectedSessionId} size={14} />
              <HeaderTitle
                key={selectedSessionId}
                title={activeTitle ?? 'Session'}
                editTitle="Rename this session"
                onRename={(next) => handleRename(selectedSessionId, next)}
              />
            </>
          )}
          {/*
            THE SESSION HEADER SLOT (RFC 0278). An anchor, not the widgets: what
            fills it needs the transcript, and the transcript is opened one level
            down in `ThreadView`. So the pane publishes a node at the right end
            of its header and the thread PORTALS into it, the same move the
            composer makes to reach the collapsed panel (`ComposerSlot`). The
            alternative was a second windowed transcript read up here, which is
            the largest read this pane makes.

            Rendered whenever a session is open — the spacer no longer belongs to
            the pane controls, because a right-aligned widget needs it even in a
            single pane that offers neither Split nor Close.
          */}
          {selectedSessionId && (
            <>
              <div className="flex-1" />
              {/* min-w-0, so its one shrinking widget (where the session
                  works, RFC 0277 S14) gives way before the title does. */}
              <div
                ref={setSessionHeaderSlot}
                className="flex min-w-0 items-center gap-1"
              />
            </>
          )}
          {(onSplit || onClose) && (
            <>
              {!selectedSessionId && <div className="flex-1" />}
              {onSplit && (
                <PanelAction
                  title="Open in split — a second pane on this conversation"
                  onClick={onSplit}
                >
                  <Columns2 size={14} />
                </PanelAction>
              )}
              {onClose && (
                <PanelAction title="Close this pane" onClick={onClose}>
                  <X size={14} />
                </PanelAction>
              )}
            </>
          )}
        </>
      }
    >
      <div className="h-full min-h-0 overflow-hidden">
        {selectedSessionId ? (
          <ThreadView
            key={selectedSessionId}
            sessionId={selectedSessionId}
            // The project that HOLDS this session, like the rename/delete
            // writes above — not the window's. Sending is a write against the
            // session's registry (`touchSession`, via `enqueueRun`), and
            // `touchSession` FILLS IN a missing entry rather than skipping, so
            // the window's id here does not no-op: it mints a phantom
            // `sessionMeta` row in a project the session is not in, while the
            // owning project's `updatedAt` never moves and its host window
            // never promotes the session. Every turn then sits at "waiting for
            // a worker" with nothing to distinguish it from a busy one.
            projectId={projectOf(selectedSessionId)}
            headerSlot={sessionHeaderSlot}
            onOpenDocument={onOpenDocument}
            // A card in the thread is the door into a session the agent
            // opened, and once the list stops showing those it is the only
            // door. It opens in THIS block, named with the project that holds
            // it — the same pair `onSelect` takes everywhere else, because a
            // pane pointed at the wrong project resolves against the wrong
            // catalog and prunes itself.
            onOpenSession={back.openFromCard}
            projects={choices}
            onSelectProject={handleMoveProject}
            messageView={messageView}
          />
        ) : (
          // The list scrolls, the composer does not: it is the one thing on
          // this screen that must be reachable without arriving at the bottom
          // of however many sessions you have.
          <div className="flex h-full min-h-0 flex-col">
            <div className="min-h-0 flex-1 overflow-y-auto">
              <SessionList
                sessions={sessions}
                activeSessionId={selectedSessionId}
                isLoading={loading}
                // The list spans projects, so the button offers the choice.
                projects={choices}
                {...(createDisabledReason ? { createDisabledReason } : {})}
                onCreate={handleCreate}
                onOpen={onSelect}
                onRename={handleRename}
                onDelete={handleDelete}
                onComplete={handleComplete}
              />
            </div>
            {/* A session that starts with the thing you came to say, rather
                than pressing New and typing into an empty thread. The project
                capsule sits on it, so where the work lands is chosen in the
                same breath as what the work is. */}
            <div className="shrink-0">
              <Composer
                onSend={handleStart}
                onAttach={attach}
                placeholder="Start a session"
                // Only the pane you are in takes the caret. Two blocks both on
                // the list would otherwise race for it on every render.
                autoFocus={focused}
                projects={choices}
                projectId={startTarget}
                onSelectProject={setStartTarget}
                // Nothing has been said in a session that does not exist yet,
                // so the choice is always open here.
                unstarted={true}
                // The picker reads its harnesses out of the devices; with none
                // it showed only Mod while the label said Claude Code.
                devices={devices}
                voice={listVoice}
              />
            </div>
          </div>
        )}
      </div>
    </Panel>
  )
}
